import { useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { ArrowLeftRight, CalendarClock, Clock, Plus, RefreshCw, Search } from "lucide-react";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import { api, formatDate } from "../api";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import Dialog from "../components/Dialog";
import ContactDrawer from "../components/crm/ContactDrawer";
import ContactPicker from "../components/crm/ContactPicker";
import CloseDialog from "../components/crm/CloseDialog";
import useNotice from "../components/crm/useNotice";
import type { ContactRow, Deal, Person } from "../components/crm/types";
import { EmptyState, ErrorState, PageHeader, PageSkeleton, monogram } from "../components/Ui";
import { BOARD_STAGES, STAGES, columnOrder, contactName, money, parseMoney, shortWhen, sinceShort, stageLabel, type StageId } from "../crm";
import MoneyInput from "../components/crm/MoneyInput";
import { useBoardDrag, type CardOrigin, type DropTarget } from "../components/crm/board-drag";

type Summary = {
  open: { deals: number; value_cents: number };
  won_this_month: { deals: number; value_cents: number };
  calls_next_7_days: number;
  new_contacts_7_days: number;
};


/** Opens a contact in the drawer through the URL (?contact=), so Back closes it and links can open it. */
export function useContactParam() {
  const [params, setParams] = useSearchParams();
  const id = Number(params.get("contact")) || null;
  /** `panel: "book"` opens the drawer on Book a call; with `bookingId` it moves that call (Reschedule). */
  const open = (contactId: number | null, panel?: "book", bookingId?: number) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    if (contactId) next.set("contact", String(contactId)); else next.delete("contact");
    if (contactId && panel) next.set("panel", panel); else next.delete("panel");
    if (contactId && panel && bookingId) next.set("booking", String(bookingId)); else next.delete("booking");
    return next;
  });
  return [id, open] as const;
}

/**
 * The agency's sales pipeline (Pyre's board, ported): a column per stage, drag a
 * card to move it, click it to open the contact. Lost and Not a fit live on Closed.
 */
export default function CrmPipeline() {
  const [view, setView] = useState<"board" | "closed">("board");
  const deals = useApi<Deal[]>(`/api/crm/deals?view=${view}`);
  const summary = useApi<Summary>("/api/crm/summary");
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [contactId, openContact] = useContactParam();
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<{ deal: Deal; stage: StageId; before: number | null } | null>(null);
  const [creating, setCreating] = useState<StageId | null>(null);
  const [notice, setNotice] = useNotice();

  const all = deals.data || [];
  const needle = query.trim().toLowerCase();
  const rows = useMemo(() => all.filter((d) => !needle || `${d.title} ${d.first_name} ${d.last_name} ${d.company} ${d.email}`.toLowerCase().includes(needle)), [all, needle]);
  const reload = () => { deals.reload(); summary.reload(); };

  /** Move a deal, put it in place in its new column, and save both. Won without an amount asks for one first. */
  async function place(deal: Deal, stage: StageId, before: number | null, extra: Record<string, unknown> = {}) {
    if (stage !== deal.stage && !Object.keys(extra).length) {
      if (stage === "won" && !deal.value_cents) { setPending({ deal, stage, before }); return; }
      if (stage === "lost") { setPending({ deal, stage, before }); return; }
    }
    // The whole column, hidden-by-search cards included, so the saved order never drops one.
    const ids = columnOrder(all, deal.id, stage, before);
    // In place first, so the card doesn't jump back while the save runs.
    deals.setData((cur) => {
      if (!cur) return cur;
      const byId = new Map(cur.map((d) => [d.id, d]));
      const moved = { ...deal, ...extra, stage } as Deal;
      const column = ids.map((id) => (id === deal.id ? moved : byId.get(id))).filter((d): d is Deal => Boolean(d));
      return [...column, ...cur.filter((d) => d.stage !== stage && d.id !== deal.id)];
    });
    try {
      if (stage !== deal.stage || Object.keys(extra).length) await api(`/api/crm/deals/${deal.id}`, { method: "PATCH", body: JSON.stringify({ stage, ...extra }) });
      await api("/api/crm/deals/order", { method: "PUT", body: JSON.stringify({ ids }) });
      if (stage !== deal.stage) setNotice(`${deal.title} moved to ${stageLabel(stage)}.`);
      summary.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "The move did not save.", true);
      deals.reload();
    }
  }

  /** The slot is an index into the cards the column shows (search hides some), without the dragged one. */
  function drop(origin: CardOrigin, target: DropTarget) {
    const deal = all.find((d) => d.id === origin.dealId);
    if (!deal) return;
    const shown = rows.filter((d) => d.stage === target.stageId && d.id !== deal.id);
    void place(deal, target.stageId as StageId, shown[target.index]?.id ?? null);
  }
  const boardRef = useRef<HTMLDivElement | null>(null);
  const { drag, ghostRef, startDrag, consumeClick } = useBoardDrag({
    scrollerRef: boardRef,
    onDrop: drop,
    onTap: (origin) => { const d = all.find((x) => x.id === origin.dealId); if (d) openContact(d.contact_id); },
  });
  const draggingDeal = drag ? all.find((d) => d.id === drag.dealId) || null : null;
  const moveMenu = (d: Deal) => canWrite && (
    // Moving without dragging: phones, keyboards, and anyone who'd rather pick.
    <span className="crm-kcard-move" data-no-drag onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <QuickMenu label={`Move ${d.title}`} align="right" trigger={<span className="crm-kcard-move-btn" title="Move to another stage"><ArrowLeftRight aria-hidden="true" /></span>}>
        {(close) => STAGES.map((s) => <QuickMenuItem key={s.id} active={s.id === d.stage} onClick={() => { close(); if (s.id !== d.stage) void place(d, s.id, null); }}>{s.label}</QuickMenuItem>)}
      </QuickMenu>
    </span>
  );

  const newDeal = canWrite && <button className="button" onClick={() => setCreating("new_lead")}><Plus aria-hidden="true" />New deal</button>;
  const s = summary.data;
  // The board holds open deals and the last 90 days' wins; the count names both, so it
  // agrees with the Open deals tile above it ("6 open, 1 won", not "7 deals").
  const won = rows.filter((d) => d.stage === "won").length;
  const shownCount = view === "board"
    ? `${rows.length - won} open${won ? `, ${won} won` : ""}`
    : `${rows.length} ${rows.length === 1 ? "deal" : "deals"}`;

  return (
    <>
      <PageHeader title="Pipeline" actions={newDeal} />
      <div className="crm-stats" aria-label="Pipeline at a glance">
        <div><span>Open deals</span><strong>{s ? s.open.deals : "—"}</strong></div>
        <div><span>Open pipeline</span><strong>{s ? money(s.open.value_cents) || "$0" : "—"}</strong></div>
        <div><span>Won in {new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "America/New_York" }).format(new Date())}</span><strong>{s ? money(s.won_this_month.value_cents) || "$0" : "—"}</strong><small>{s ? `${s.won_this_month.deals} ${s.won_this_month.deals === 1 ? "deal" : "deals"}` : ""}</small></div>
        <div><span>Calls next 7 days</span><strong>{s ? s.calls_next_7_days : "—"}</strong></div>
        <div><span>New contacts this week</span><strong>{s ? s.new_contacts_7_days : "—"}</strong></div>
      </div>
      <div className="crm-toolbar">
        <div className="tabs" role="tablist" aria-label="Pipeline views">
          <button role="tab" aria-selected={view === "board"} className={view === "board" ? "active" : ""} onClick={() => setView("board")}>Board</button>
          <button role="tab" aria-selected={view === "closed"} className={view === "closed" ? "active" : ""} onClick={() => setView("closed")}>Closed</button>
        </div>
        <label className="search-control"><Search aria-hidden="true" /><input aria-label="Search deals" placeholder="Search deals" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
        <span className="toolbar-end">
          <span className="result-count" aria-live="polite">{deals.loading ? "Loading…" : shownCount}</span>
          <button className="icon-button" aria-label="Refresh deals" title="Refresh deals" disabled={deals.refreshing} onClick={reload}><RefreshCw className={deals.refreshing ? "spin" : ""} aria-hidden="true" /></button>
        </span>
      </div>
      {/* Floats over the page: an inline line here pushed the board down after every move and back up 6 s later. */}
      {notice && <p className="crm-toast" role="status">{notice}</p>}
      {deals.loading && !deals.data ? <PageSkeleton cards={0} />
        : deals.error ? <ErrorState message={deals.error} onRetry={reload} />
        : view === "closed" ? (
          !rows.length ? <section className="panel"><EmptyState title="Nothing closed yet" description="Lost and not-a-fit deals show up here, and won deals once they leave the board after 90 days." /></section> : (
            <section className="panel table-wrap">
              <table className="module-table crm-table">
                <thead><tr><th>Deal</th><th>Contact</th><th>Stage</th><th className="num">Amount</th><th>Owner</th><th>Closed</th><th>Reason</th></tr></thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.id} className="is-clickable" onClick={() => openContact(d.contact_id)}>
                      <td data-label="Deal"><button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); openContact(d.contact_id); }}>{d.title}</button></td>
                      <td data-label="Contact">{contactName(d)}</td>
                      <td data-label="Stage"><span className={`crm-stage s-${d.stage}`}>{stageLabel(d.stage)}</span></td>
                      <td data-label="Amount" className="num" data-empty={d.value_cents ? undefined : ""}>{money(d.value_cents) || "—"}</td>
                      <td data-label="Owner">{d.owner_name || "Unassigned"}</td>
                      <td data-label="Closed">{formatDate(d.closed_at)}</td>
                      <td data-label="Reason" className="crm-muted crm-reason" data-empty={closeReason(d) ? undefined : ""}>{closeReason(d)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )
        ) : !all.length ? (
          <section className="panel"><EmptyState title="No deals yet" description="Leads from your forms and booking pages land here. You can also add a deal by hand." action={newDeal || undefined} /></section>
        ) : (
          // Orbit's board, Trello style: fixed-width columns side by side (the row scrolls
          // sideways), each column scrolls its own cards, hairlines between columns.
          <div className="crm-kanban" ref={boardRef} aria-label="Pipeline board">
            {BOARD_STAGES.map((stage) => {
              const items = rows.filter((d) => d.stage === stage.id);
              const total = items.reduce((sum, d) => sum + Number(d.value_cents || 0), 0);
              const visible = drag ? items.filter((d) => d.id !== drag.dealId) : items;
              const slot = drag && drag.target.stageId === stage.id ? Math.max(0, Math.min(drag.target.index, visible.length)) : null;
              const placeholder = slot !== null && drag ? <div className="crm-kslot" aria-hidden="true" style={{ height: drag.height }} /> : null;
              return (
                <section
                  key={stage.id}
                  className={`crm-kcol${slot !== null ? " over" : ""}`}
                  data-stage-id={stage.id}
                  aria-label={`${stage.label}, ${items.length} ${items.length === 1 ? "deal" : "deals"}`}
                >
                  <header className="crm-kcol-head">
                    <span className={`crm-kpill s-${stage.id}`}><span className={`crm-dot s-${stage.id}`} aria-hidden="true" />{stage.label}</span>
                    <span className="crm-kcount">{items.length}</span>
                    {total > 0 && <span className="crm-ktotal">{money(total)}</span>}
                  </header>
                  <div className="crm-kcol-body" data-col-body>
                    {visible.map((d, i) => (
                      <DealCardSlot key={d.id} before={slot === i ? placeholder : null}>
                        <DealCard
                          deal={d} stageLabel={stage.label} draggable={canWrite} moveMenu={moveMenu(d)}
                          onPointerDown={(e) => startDrag(e, { dealId: d.id, stageId: stage.id, index: i })}
                          onOpen={() => { if (!consumeClick()) openContact(d.contact_id); }}
                        />
                      </DealCardSlot>
                    ))}
                    {slot === visible.length ? placeholder : null}
                    {!items.length && slot === null && <p className="crm-kempty">No deals</p>}
                    {canWrite && (
                      <button type="button" className="crm-kadd" onClick={() => setCreating(stage.id)}><Plus aria-hidden="true" />Add deal</button>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      {/* The card under the cursor, on the body so no column's overflow can clip it. */}
      {drag && draggingDeal && createPortal(
        <div ref={ghostRef} className="crm-kghost" style={{ width: drag.width, transform: `translate3d(${drag.originX}px, ${drag.originY}px, 0) rotate(1.5deg)` }}>
          <DealCard deal={draggingDeal} stageLabel={stageLabel(draggingDeal.stage)} ghost />
        </div>,
        document.body,
      )}
      {pending && (
        <CloseDialog
          pending={pending}
          onCancel={() => setPending(null)}
          onConfirm={(extra) => { const p = pending; setPending(null); void place(p.deal, p.stage, p.before, extra); }}
        />
      )}
      {creating && <NewDealDialog stage={creating} onClose={() => setCreating(null)} onCreated={(msg, cid) => { setCreating(null); setNotice(msg); reload(); openContact(cid); }} />}
      {contactId && <ContactDrawer key={contactId} id={contactId} onClose={() => openContact(null)} onChanged={reload} />}
    </>
  );
}

/** Why a closed deal closed: the lost reason staff gave, or the answers that made it not a fit. */
const closeReason = (d: Deal) => d.lost_reason || (d.stage === "dq" ? (d.dq_reasons || []).join("; ") || "Not a fit" : "");

/** A slot (while dragging) above a card. Fragments keep the column's flex gap the only spacing. */
function DealCardSlot({ before, children }: { before: ReactNode; children: ReactNode }) {
  return <>{before}{children}</>;
}

/** One deal on the board; the drag ghost draws the same card so the two cannot drift apart. */
function DealCard({ deal: d, stageLabel: stage, ghost, draggable, moveMenu, onPointerDown, onOpen }: {
  deal: Deal; stageLabel: string; ghost?: boolean; draggable?: boolean; moveMenu?: ReactNode;
  onPointerDown?: (e: React.PointerEvent<HTMLElement>) => void; onOpen?: () => void;
}) {
  const person = contactName(d);
  return (
    <article
      className={`crm-kcard${ghost ? " is-ghost" : ""}${draggable ? " can-drag" : ""}`}
      data-deal-id={ghost ? undefined : d.id}
      onPointerDown={onPointerDown}
      onClick={onOpen}
      onKeyDown={ghost ? undefined : (e) => { if (e.key === "Enter") onOpen?.(); }}
      // Native drag would fight the pointer gesture (text and links start one on their own).
      onDragStart={(e) => e.preventDefault()}
      tabIndex={ghost ? undefined : 0}
      aria-label={ghost ? undefined : `${d.title}, ${stage}`}
      aria-hidden={ghost || undefined}
    >
      <div className="crm-kcard-top">
        <p className="crm-kcard-title">{d.title}</p>
        {d.owner_name
          ? <span className="crm-kav" title={`Owner: ${d.owner_name}`} aria-label={`Owner: ${d.owner_name}`}>{monogram(d.owner_name)}</span>
          : <span className="crm-kav is-empty" title="Unassigned" aria-label="Unassigned" />}
      </div>
      {d.value_cents > 0 && <p className="crm-kcard-value">{money(d.value_cents)}</p>}
      {d.next_call_at && <p className="crm-kcard-call" title="Next call"><CalendarClock aria-hidden="true" />{shortWhen(d.next_call_at)}</p>}
      <div className="crm-kcard-foot">
        {person !== d.title ? <span className="crm-kcard-person"><span className="crm-kav is-contact" aria-hidden="true">{monogram(person)}</span><span>{person}</span></span> : <span />}
        <span className="crm-kcard-age" title={`In ${stage} for ${sinceShort(d.stage_changed_at)}`}><Clock aria-hidden="true" />{sinceShort(d.stage_changed_at)}</span>
        {!ghost && moveMenu}
      </div>
    </article>
  );
}

function NewDealDialog({ stage, onClose, onCreated }: { stage: StageId; onClose: () => void; onCreated: (msg: string, contactId: number) => void }) {
  const people = useApi<Person[]>("/api/tasks/assignees");
  const { user } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", title: "", stage: stage as string, amount: "", owner_id: user ? String(user.id) : "" });
  const [contactMode, setContactMode] = useState<"existing" | "new">("existing");
  const [contact, setContact] = useState<ContactRow | null>(null);
  const dealName = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (contactMode === "existing" && !contact) { setError("Choose a contact to link this deal to."); return; }
    const cents = parseMoney(f.amount);
    if (cents === null) { setError("Enter the amount in dollars, like 12,500 or 12.5k."); return; }
    if (f.stage === "won" && !cents) { setError("A won deal needs its contract amount."); return; }
    setBusy(true); setError("");
    try {
      const deal = await api<{ id: number; contact_id: number; title: string }>("/api/crm/deals", {
        method: "POST",
        body: JSON.stringify({
          ...(contactMode === "existing" ? { contact_id: contact!.id }
            : { contact: { first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone, company: f.company } }),
          title: f.title, stage: f.stage, value_cents: cents, owner_id: f.owner_id || null,
        }),
      });
      onCreated(`${deal.title} added to ${stageLabel(f.stage)}.`, deal.contact_id);
    } catch (err) { setError(err instanceof Error ? err.message : "The deal could not be added."); }
    finally { setBusy(false); }
  }
  return (
    <Dialog title="New deal" onClose={onClose}>
      <form className="operation-form crm-edit" onSubmit={submit}>
        <div className="tabs" role="group" aria-label="Deal contact">
          <button type="button" className={contactMode === "existing" ? "active" : ""} aria-pressed={contactMode === "existing"}
            disabled={busy} onClick={() => { setContactMode("existing"); setError(""); }}>Existing contact</button>
          <button type="button" className={contactMode === "new" ? "active" : ""} aria-pressed={contactMode === "new"}
            disabled={busy} onClick={() => { setContactMode("new"); setError(""); }}>New contact</button>
        </div>
        {contactMode === "existing" && <ContactPicker value={contact} disabled={busy} onChange={(value) => {
          setContact(value); setError("");
          if (value) window.requestAnimationFrame(() => dealName.current?.focus());
        }} />}
        <div className="crm-edit-grid">
          {contactMode === "new" && <>
          <label>First name<input value={f.first_name} onChange={set("first_name")} maxLength={80} required /></label>
          <label>Last name<input value={f.last_name} onChange={set("last_name")} maxLength={80} /></label>
          <label>Email<input type="email" value={f.email} onChange={set("email")} maxLength={200} /></label>
          <label>Phone<input type="tel" value={f.phone} onChange={set("phone")} maxLength={40} /></label>
          <label>Company<input value={f.company} onChange={set("company")} maxLength={160} /></label>
          </>}
          <label className="wide">Deal name<input ref={dealName} value={f.title} onChange={set("title")} maxLength={200} placeholder={contactMode === "existing" && contact ? contact.company || contactName(contact) : "Company or contact name"} /></label>
          <label>Stage
            <select value={f.stage} onChange={set("stage")}>{STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select>
          </label>
          <label><span>Contract amount <span className="crm-muted">Optional</span></span><MoneyInput value={f.amount} onChange={(v) => setF((x) => ({ ...x, amount: v }))} /></label>
          <label>Owner
            <select value={f.owner_id} onChange={set("owner_id")}>
              <option value="">Unassigned</option>
              {(people.data || []).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </label>
        </div>
        {error && <p className="crm-error" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button" disabled={busy || (contactMode === "existing" && !contact)}>{busy ? "Adding…" : "Add deal"}</button>
        </div>
      </form>
    </Dialog>
  );
}
